import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { voiceApi, type VoicemailHeardResponse } from "api/voice";

/**
 * CRM-1777: reports a voicemail as played, once per mounted player, from any
 * surface (Inbox, Calls, the lead's Activity log). The first play anywhere
 * marks it heard for the whole team; every report adds a row to the recording
 * access history. A failed report is retried on the next play rather than
 * shown — it costs the listener nothing.
 */
export const useVoicemailPlayed = (
  callId: number | string | null | undefined,
  { onPlayed }: { onPlayed?: (response: VoicemailHeardResponse) => void } = {}
) => {
  const queryClient = useQueryClient();
  const reportedRef = useRef(false);
  const onPlayedRef = useRef(onPlayed);
  useEffect(() => {
    onPlayedRef.current = onPlayed;
  }, [onPlayed]);

  const { mutate } = useMutation({
    mutationKey: ["VOICEMAIL", "PLAYED", "UPDATE"],
    mutationFn: (id: number | string) => voiceApi.markVoicemailPlayed(id),
    onSuccess: (response) => {
      onPlayedRef.current?.(response);
      // The Calls log and its drawer show who played it; recordings sit
      // outside CALLS, so a playing player is not refetched.
      queryClient.invalidateQueries({ queryKey: ["CALLS"] });
    },
    onError: () => {
      reportedRef.current = false;
    }
  });

  return useCallback(() => {
    if (reportedRef.current || callId == null) return;
    reportedRef.current = true;
    mutate(callId);
  }, [callId, mutate]);
};
